import { useRef } from 'react'
import styles from './TopServices.module.scss'
import Button from 'components/common/Button/Button'
import animationStyles from 'components/common/Animation/Animation.module.scss'
import useElementVisible from 'components/common/Animation/useElementVisible'

const TopServices: React.FC = () => {
  // refとisVisibleを配列で管理
  const refs = [
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
  ]
  const isVisibleArray = refs.map((ref) => useElementVisible(ref, 0.1))

  const services = [
    {
      titleEn: 'AIDD',
      titleJa: 'AI駆動開発',
      text: (
        <>
          AI駆動開発の研修から、AIエージェント基盤の構築、内製化までを、自社で磨いた実践メソッドで。現場に点在するAI活用を組織の開発標準に変え、チームが自走できる状態まで支援します。
        </>
      ),
      links: [
        { href: '/services/aidd/', text: 'AI駆動開発TOP', external: true },
        { href: '/services/aidd/internalization/', text: 'AI駆動開発内製化サービス', external: true },
        { href: '/services/aidd/ai-agent-infrastructure/', text: 'AIエージェント基盤構築サービス', external: true },
        { href: '/services/aidd/training/', text: 'AI駆動開発研修', external: true },
      ],
    },
    {
      titleEn: 'Studio',
      titleJa: 'プロダクト開発',
      text: (
        <>
          UXリサーチからデザイン、開発、改善までを、AIでつながったひとつのチームで。新規プロダクトの立ち上げから既存サービスのUX/UI刷新、デザインシステム構築まで支援します。
        </>
      ),
      links: [
        { href: '/services/studio/ai-ux-sprint/', text: 'AI-UXスプリント', external: true },
        { href: '/services/studio/ux-workshop/', text: 'UX/UIワークショップ', external: true },
        { href: '/services/studio/application-development/', text: 'アプリケーション開発' },
        { href: '/services/studio/modernization/', text: 'モダナイゼーション' },
        { href: '/services/studio/uxui-improvement/', text: 'UX/UI改善' },
        { href: '/services/studio/design-system/', text: 'デザインシステム構築支援' },
      ],
    },
    {
      titleEn: 'Forge',
      titleJa: '開発モダナイゼーション',
      text: (
        <>
          クラウドリフト後のサーバーレス化から、マイクロサービス化、CI/CDの整備、データ統合基盤の構築までを、設計と実装の両面で。システムと開発プロセスを、AI時代に通用するかたちへ段階的に引き上げます。
        </>
      ),
      links: [{ href: '/services/forge/cloudnative/', text: 'クラウドネイティブ' }],
    },
    {
      titleEn: 'Products',
      titleJa: '製品・サービス',
      products: [
        {
          href: 'https://phennec.ai/',
          text: 'Phennec',
          desc: 'AIエージェントの真価を引き出す「コンテキスト・エンジニアリング」基盤',
          imgSrc: '/img/services/services_phennec.webp',
          external: true,
        },
        {
          href: '/products/flamingo/',
          text: 'flamingo',
          desc: 'ウェブシステムのUX/UIデザインをモダナイズするSaaS',
          imgSrc: '/img/services/services_flamingo.webp',
          external: true,
        },
        {
          href: '/products/zebra/',
          text: 'ZEBRA',
          desc: '最適にセグメント配信可能なCRM型のプッシュ通知エンジン',
          imgSrc: '/img/services/services_zebra.webp',
          external: false,
        },
      ],
    },
  ]

  return (
    <section className={styles.topServices}>
      <div className={styles.inner}>
        <h2
          className={`${styles.topServices__title} ${animationStyles.fade} ${
            isVisibleArray[0] ? animationStyles.fadeOn : ''
          }`}
          ref={refs[0]}
        >
          提供サービス
        </h2>

        {services.map((service, index) => (
          <article
            key={index}
            className={`${styles.topSL} ${animationStyles.fade} ${
              isVisibleArray[index + 1] ? animationStyles.fadeOn : ''
            }`}
            ref={refs[index + 1]}
          >
            <h3 className={styles.topSL__title}>
              <span className={styles.topSL__title__en}>{service.titleEn}</span>
              <span className={styles.topSL__title__ja}>{service.titleJa}</span>
            </h3>
            {service.text && <p className={styles.topSL__text}>{service.text}</p>}
            <ul className={styles.topSL__items}>
              {service.links?.map((link, idx) => (
                <li key={idx} className={styles.topSL__items__item}>
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : ''}
                    className={link.external ? styles.window : ''}
                  >
                    <span>{link.text}</span>
                  </a>
                </li>
              ))}
              {service.products?.map((product, idx) => (
                <li key={idx} className={styles.topSL__items__item}>
                  <a
                    href={product.href}
                    target={product.external ? '_blank' : ''}
                    className={product.external ? styles.window : ''}
                  >
                    <h4 className={styles.topSL__items__item__subT}>
                      <span>{product.text}</span>
                    </h4>
                    <p className={styles.topSL__items__item__text}>{product.desc}</p>
                    <figure className={styles.topSL__items__item__img}>
                      <img
                        src={product.imgSrc}
                        alt={product.text}
                        width={480}
                        height={360}
                        className={styles.topSL__items__item__img__image}
                      />
                    </figure>
                  </a>
                </li>
              ))}
            </ul>
          </article>
        ))}

        <div className={styles.topSL__contact}>
          <p className={styles.topSL__contact__text}>貴社が解決したい課題、達成したいミッションをお聞かせください。</p>
          <Button text="相談してみる" href="/contact/" red="red" />
        </div>
      </div>
    </section>
  )
}

export default TopServices
